import { useContext, useEffect, useMemo, useSyncExternalStore } from "react";
import { PartsSubscriptionContext } from "./PartsSubscriptionProvider";
import { Part } from "../../../reducers/models/parts";
import { InventoryResponse } from "../../../reducers/componentSearch";

function getServerSnapshot(): undefined {
  return undefined;
}

/**
 * Live view of a part. All parts on the page share one websocket via
 * PartsSubscriptionProvider (mounted in _app.tsx).
 */
export default function usePart(partId: string | null) {
  const store = useContext(PartsSubscriptionContext);
  if (store === null) {
    throw new Error("usePart must be used within a PartsSubscriptionProvider");
  }

  useEffect(() => {
    if (partId === null) {
      return undefined;
    }
    // follow() returns the matching unfollow, which runs when this component
    // unmounts or partId changes.
    return store.follow(partId);
  }, [store, partId]);

  const entry = useSyncExternalStore(
    store.subscribe,
    () => {
      return store.getPart(partId);
    },
    getServerSnapshot,
  );

  return {
    part: entry ?? null,
    loading: partId !== null && entry === undefined,
  };
}

function getServerSnapshotVersion(): number {
  return 0;
}

export interface PartAndInventory {
  part: Part;
  inventory: InventoryResponse;
}

/**
 * Live view of several parts along with their containers and allocations.
 * Inventory is only ever sent to admins and engineers, so this is for internal
 * pages. `loading` stays true until every part has arrived; a part that does
 * not exist is left out of `parts`.
 */
export function usePartsWithInventory(partIds: string[]) {
  const store = useContext(PartsSubscriptionContext);
  if (store === null) {
    throw new Error(
      "usePartsWithInventory must be used within a PartsSubscriptionProvider",
    );
  }
  const partIdsKey = useMemo(() => {
    return [...new Set(partIds)].sort().join(",");
  }, [partIds]);

  useEffect(() => {
    if (partIdsKey === "") {
      return undefined;
    }
    const unfollows = partIdsKey.split(",").map((partId) => {
      return store.followWithInventory(partId);
    });
    return () => {
      unfollows.forEach((unfollow) => {
        unfollow();
      });
    };
  }, [store, partIdsKey]);

  const version = useSyncExternalStore(
    store.subscribe,
    store.getVersion,
    getServerSnapshotVersion,
  );

  return useMemo(() => {
    const parts = new Map<string, PartAndInventory>();
    let loading = false;
    const ids = partIdsKey === "" ? [] : partIdsKey.split(",");
    ids.forEach((partId) => {
      const part = store.getPart(partId);
      const inventory = store.getInventory(partId);
      if (part === null) {
        return;
      }
      if (part === undefined || inventory === undefined || inventory === null) {
        loading = true;
        return;
      }
      parts.set(partId, { part, inventory });
    });
    return { parts, loading };
    // The store is mutable, so its version stands in for its contents
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, partIdsKey, version]);
}
